import React from 'react';

// SPINNER COMPONENT (BARU DITAMBAHKAN DAN DIEKSPOR)
export const Spinner = ({ size = 'h-8 w-8', color = 'border-brand-purple' }) => (
    <div className={`loader ease-linear rounded-full border-4 border-t-4 border-gray-200 ${size}`}>
        <style>{`
            .loader {
                border-top-color: #6D28D9; /* Fallback color */
                animation: spinner 1.5s linear infinite;
            }
            @keyframes spinner {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `}</style>
    </div>
);


export const Input = ({ id, label, type = 'text', value, onChange, required = false, ...props }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-bold text-gray-700 mb-2">
            {label}
        </label>
        <input
            id={id}
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-purple focus:border-transparent transition"
            {...props}
        />
    </div>
);

export const Button = ({ children, onClick, type = 'button', disabled = false, variant = 'primary', small = false, loading = false, className = '', ...props }) => {
    const baseClasses = "font-bold rounded-2xl shadow-lg transition-all duration-300 transform focus:outline-none focus:ring-4 focus:ring-opacity-50 flex items-center justify-center space-x-2";
    const sizeClasses = small ? "py-3 px-6 text-sm" : "w-full py-4 px-6";
    const variants = {
        primary: "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 focus:ring-purple-300",
        secondary: "bg-gradient-to-r from-blue-400 to-cyan-400 text-white hover:from-blue-500 hover:to-cyan-500 focus:ring-blue-300",
        danger: "bg-gradient-to-r from-red-400 to-pink-400 text-white hover:from-red-500 hover:to-pink-500 focus:ring-red-300",
        success: "bg-gradient-to-r from-green-400 to-emerald-400 text-white hover:from-green-500 hover:to-emerald-500 focus:ring-green-300"
    };
    const finalDisabled = disabled || loading;
    const disabledClasses = "disabled:bg-gray-300 disabled:cursor-not-allowed disabled:transform-none disabled:opacity-50";

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={finalDisabled}
            className={`${baseClasses} ${sizeClasses} ${variants[variant]} ${finalDisabled ? disabledClasses : 'hover:scale-105 active:scale-95'} ${className}`}
            {...props}
        >
            {loading && <Spinner size="h-5 w-5" />}
            <span>{children}</span>
        </button>
    );
};

// Modern Radio Button Component with childish design
export const RadioGroup = ({ name, options, value, onChange, className = '' }) => (
    <div className={`flex flex-wrap gap-3 ${className}`}>
        {options.map((option) => (
            <label
                key={option.value}
                className={`
                    flex items-center justify-center px-6 py-3 rounded-2xl border-2 cursor-pointer
                    transition-all duration-300 transform hover:scale-105 active:scale-95
                    font-semibold text-sm shadow-md hover:shadow-lg
                    ${value === option.value 
                        ? 'border-purple-400 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 shadow-lg' 
                        : 'border-gray-200 bg-white text-gray-600 hover:border-purple-200 hover:bg-purple-50'
                    }
                `}
            >
                <input
                    type="radio"
                    name={name}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => onChange(e.target.value)}
                    className="sr-only"
                />
                <span className="flex items-center space-x-2">
                    {option.emoji && <span className="text-lg">{option.emoji}</span>}
                    <span>{option.label}</span>
                </span>
            </label>
        ))}
    </div>
);

// Scale Radio Group for GPPH (0-3 scale)
export const ScaleRadioGroup = ({ name, value, onChange, className = '' }) => {
    const scaleOptions = [
        { value: '0', label: 'Tidak Pernah', emoji: '😊', color: 'from-green-100 to-emerald-100 border-green-300 text-green-700' },
        { value: '1', label: 'Kadang-kadang', emoji: '🙂', color: 'from-yellow-100 to-amber-100 border-yellow-300 text-yellow-700' },
        { value: '2', label: 'Sering', emoji: '😐', color: 'from-orange-100 to-red-100 border-orange-300 text-orange-700' },
        { value: '3', label: 'Selalu', emoji: '😟', color: 'from-red-100 to-pink-100 border-red-300 text-red-700' }
    ];

    return (
        <div className={`grid grid-cols-2 gap-3 ${className}`}>
            {scaleOptions.map((option) => (
                <label
                    key={option.value}
                    className={`
                        flex flex-col items-center justify-center p-4 rounded-2xl border-2 cursor-pointer
                        transition-all duration-300 transform hover:scale-105 active:scale-95
                        font-semibold text-xs shadow-md hover:shadow-lg min-h-[80px]
                        ${value === option.value 
                            ? `bg-gradient-to-r ${option.color} shadow-lg scale-105` 
                            : 'border-gray-200 bg-white text-gray-600 hover:border-purple-200 hover:bg-purple-50'
                        }
                    `}
                >
                    <input
                        type="radio"
                        name={name}
                        value={option.value}
                        checked={value === option.value}
                        onChange={(e) => onChange(e.target.value)}
                        className="sr-only"
                    />
                    <span className="text-2xl mb-1">{option.emoji}</span>
                    <span className="text-center leading-tight">{option.label}</span>
                </label>
            ))}
        </div>
    );
};

export const LoadingScreen = ({ message = "Memuat..." }) => (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white">
        <Spinner size="h-12 w-12" />
        <p className="text-brand-muted mt-4">{message}</p>
    </div>
);

export const Card = ({ children, className = '', ...props }) => (
    <div className={`bg-white rounded-xl shadow-sm p-4 ${className}`} {...props}>
        {children}
    </div>
);

export const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md m-4">
                <div className="p-4 border-b flex justify-between items-center">
                    <h3 className="text-lg font-bold text-brand-dark">{title}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl">&times;</button>
                </div>
                <div className="p-6">
                    {children}
                </div>
            </div>
        </div>
    );
};
